import { Form, Tag } from 'antd';
import {
	Modal,
	Toggle,
	Input,
	Button,
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/design-system';
import { Icon } from '@hydrodigital/icon';
import React, { useState, useEffect } from 'react';
import Map, { Marker, NavigationControl } from 'react-map-gl';
import Pin from './pin';
import './index.scss';
import { InfoCircleOutlined } from '@ant-design/icons';
import sewerIcon from '@/assets/img/sewerIcon.png';
import waterIcon from '@/assets/img/waterIcon.png';

const mapboxApiAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const initialStyle = {
	position: 'relative',
	width: '100%',
	height: '100%',
};
// Viewport settings
const initialViewState = {
	longitude: -122,
	latitude: 37,
	zoom: 1,
	pitch: 0,
	bearing: 0,
	minZoom: 0,
};

export default function CreateLocationModal({
	isModalOpen,
	selectLocation,
	onAcept,
	onCancel,
	isEdit,
}) {
	const organizationSettings = localStorage.getItem('organizationSettings');
	const mapStyle =
		JSON.parse(organizationSettings)?.mapSettings?.settings?.style ||
		'mapbox://styles/mapbox/light-v11';

	const [form] = Form.useForm();
	const [viewport, setViewport] = useState(initialViewState);
	const [marker, setMarker] = useState({
		latitude: 37,
		longitude: -122,
	});
	const [coordinates, setCoordinates] = useState({ latitude: 0, longitude: 0 });

	const [longitudeInput, setLongitudeInput] = useState('');
	const [latitudeInput, setLatitudeInput] = useState('');

	const networkTypeOptions = [
		{
			value: 'SEWER',
			label: 'Drainage',
			icon: <img style={{ width: '18px', height: '18px' }} src={sewerIcon} alt="sewerIcon" />,
		},
		{
			value: 'WATER',
			label: 'Water',
			icon: <img style={{ width: '18px', height: '18px' }} src={waterIcon} alt="waterIcon" />,
		},
	];

	useEffect(() => {
		const current = coordinates?.longitude;
		setLongitudeInput(current !== undefined && current !== null ? String(current) : '');
	}, [coordinates?.longitude]);

	useEffect(() => {
		const current = coordinates?.latitude;
		setLatitudeInput(current !== undefined && current !== null ? String(current) : '');
	}, [coordinates?.latitude]);

	useEffect(() => {
		if (!isModalOpen) {
			form.resetFields();
			setMarker({ latitude: 0, longitude: 0 });
			setCoordinates({ latitude: 0, longitude: 0 });
			setViewport(initialViewState);
		}
	}, [isModalOpen]);

	const onFinish = (values) => {
		const { name, longitude, latitude, zoom, visible, networkType } = values;

		const locationData = {
			name,
			longitude,
			latitude,
			zoom: Math.round(zoom),
			visible,
			...(isEdit && selectLocation ? { id: selectLocation.id } : {}),
			networkType: networkType === '' ? null : networkType,
		};
		console.log('Data being sent to the service:', locationData);

		form.resetFields(); // 🧹 limpia el formulario
		setMarker({ latitude: 0, longitude: 0 });
		setCoordinates({ latitude: 0, longitude: 0 });
		setViewport(initialViewState);
		onAcept(locationData, selectLocation);
	};

	useEffect(() => {
		if (isModalOpen && selectLocation && form) {
			initForm();
		}
	}, [selectLocation, isModalOpen, form]);

	function initForm() {
		if (selectLocation && selectLocation.value) {
			const location = JSON.parse(selectLocation?.value.toString());
			form.setFieldsValue({
				name: isEdit ? location.name : '',
				latitude: location.latitude,
				longitude: location.longitude,
				zoom: location.zoom,
				visible: location.visible,
				networkType: location.networkType,
			});
			setViewport({
				...viewport,
				longitude: parseFloat(location.longitude),
				latitude: parseFloat(location.latitude),
				zoom: parseFloat(location.zoom),
			});
			setMarker({
				longitude: parseFloat(location.longitude),
				latitude: parseFloat(location.latitude),
			});
			setCoordinates({
				longitude: parseFloat(location.longitude),
				latitude: parseFloat(location.latitude),
			});
		} else {
			form.setFieldsValue({
				name: '',
				latitude: 0,
				longitude: 0,
				zoom: 1,
				visible: true,
				networkType: '',
			});
			setViewport({
				...viewport,
				longitude: 0,
				latitude: 0,
				zoom: 1,
			});
			setMarker({
				longitude: 0,
				latitude: 0,
			});
			setCoordinates({
				longitude: 0,
				latitude: 0,
			});
		}
	}

	const selectedNetworkType = Form.useWatch('networkType', form);

	useEffect(() => {
		if (form && viewport) {
			form.setFieldsValue({
				zoom: viewport.zoom,
			});
		}
	}, [viewport, form]);

	function renderNetworkIcon(networkType) {
		if (networkType === 'SEWER') {
			return <img style={{ width: '18px', height: '18px' }} src={sewerIcon} alt="sewerIcon" />;
		}
		if (networkType === 'WATER') {
			return <img style={{ width: '18px', height: '18px' }} src={waterIcon} alt="waterIcon" />;
		}
		return null;
	}

	console.log(`networkTypeOptions: ${networkTypeOptions}`);
	return (
		<Modal
			isOpen={isModalOpen}
			onClose={() => {
				form.resetFields();
				setMarker({ latitude: 0, longitude: 0 });
				setCoordinates({ latitude: 0, longitude: 0 });
				setViewport(initialViewState);
				onCancel();
			}}
			size="xl"
			closeOnOverlayClick={true}
			closeOnEscape={true}
			showCloseButton={true}
			testId="create-location-modal"
		>
			<Modal.Header>
				<div className="flex column pl-3 pr-3">
					<span className="text-title-page pt-2 pb-2" style={{ fontSize: '1rem' }}>
						{isEdit ? 'EDIT LOCATION' : 'ADD LOCATION'}
					</span>
					<div className="separator" />
					<span className="pt-2 pb-2" style={{ color: 'black' }}>
						Drag the map and position your location
					</span>
				</div>
			</Modal.Header>

			<Modal.Content>
				<div className="grid" style={{ gridTemplateColumns: '40% 60%' }}>
					<div className="flex column pl-3 pr-3">
						<Form
							form={form}
							key="create-location-form"
							onFinish={onFinish}
							initialValues={{
								visible: true,
							}}
							layout="vertical"
						>
							<Form.Item
								name="name"
								label="Location name"
								rules={[
									{
										required: true,
										message: 'Please input the location name',
									},
								]}
							>
								<Input size="small" />
							</Form.Item>
							<Form.Item
								name="longitude"
								label="Longitude"
								rules={[{ required: true, message: 'Please input the longitude' }]}
							>
								<Tag
									style={{ marginBottom: '5px' }}
									icon={<InfoCircleOutlined style={{ fontWeight: 800 }} />}
									color="processing"
								>
									<span style={{ fontWeight: 700 }}>Only values between -180 and 180</span>
								</Tag>
								<Input
									type="number"
									size="small"
									value={longitudeInput}
									style={{ width: '100%' }}
									min={-180}
									max={180}
									step={0.0001}
									inputMode="decimal"
									onChange={(e) => {
										const raw = e.target.value;

										// Allow partial input: "-", "-1", "-1.", "-1.2" etc.
										const isPartial = /^-?$|^-?\d*\.?\d*$/.test(raw);
										if (!isPartial) return;

										setLongitudeInput(raw);

										// If we have a numeric value, clamp and update coordinates/form
										if (raw !== '' && raw !== '-') {
											const parsed = parseFloat(raw);
											if (!Number.isNaN(parsed)) {
												const clamped = Math.max(-180, Math.min(180, parsed));
												setCoordinates({ ...coordinates, longitude: clamped });
												form.setFieldValue('longitude', clamped);
											}
										}
									}}
									onBlur={() => {
										try {
											// Finalize: parse/clamp if possible; otherwise revert to last valid
											const raw = longitudeInput;
											if (raw === '' || raw === '-') {
												setLongitudeInput(
													coordinates.longitude !== undefined ? String(coordinates.longitude) : '',
												);
												return;
											}
											const parsed = parseFloat(raw);
											if (!Number.isNaN(parsed)) {
												const clamped = Math.max(-180, Math.min(180, parsed));
												setLongitudeInput(String(clamped));
												setCoordinates({ ...coordinates, longitude: clamped });
												form.setFieldValue('longitude', clamped);

												const long = clamped;
												const lat = coordinates.latitude;
												if (long !== undefined && lat !== undefined) {
													setViewport({
														...viewport,
														longitude: long,
													});
													setMarker({
														...marker,
														longitude: long,
													});
												}
											}
										} catch (error) {
											console.error('131', error);
										}
									}}
								/>
							</Form.Item>
							<Form.Item
								name="latitude"
								label="Latitude"
								rules={[{ required: true, message: 'Please input the latitude' }]}
							>
								<Tag
									style={{ marginBottom: '5px' }}
									icon={<InfoCircleOutlined style={{ fontWeight: 800 }} />}
									color="processing"
								>
									<span style={{ fontWeight: 700 }}>Only values between -90 and 90</span>
								</Tag>
								<Input
									type="number"
									size="small"
									value={latitudeInput}
									style={{ width: '100%' }}
									min={-90}
									max={90}
									step={1}
									inputMode="decimal"
									onChange={(e) => {
										const raw = e.target.value;

										// Allow partial input: "-", "-8", "-8.", "-8.1", "9", "9.0"
										const isPartial = /^-?$|^-?\d*\.?\d*$/.test(raw);
										if (!isPartial) return;

										setLatitudeInput(raw);

										// If value is numeric (not empty or just "-"), clamp and update
										if (raw !== '' && raw !== '-') {
											const parsed = parseFloat(raw);
											if (!Number.isNaN(parsed)) {
												const clamped = Math.max(-90, Math.min(90, parsed));
												setCoordinates({ ...coordinates, latitude: clamped });
												form.setFieldValue('latitude', clamped);
											}
										}
									}}
									onBlur={() => {
										try {
											const raw = latitudeInput;

											// Revert to last valid if empty or just "-"
											if (raw === '' || raw === '-') {
												setLatitudeInput(
													coordinates.latitude !== undefined ? String(coordinates.latitude) : '',
												);
												return;
											}

											const parsed = parseFloat(raw);
											if (!Number.isNaN(parsed)) {
												const clamped = Math.max(-90, Math.min(90, parsed));
												setLatitudeInput(String(clamped));
												setCoordinates({ ...coordinates, latitude: clamped });
												form.setFieldValue('latitude', clamped);

												const long = coordinates.longitude;
												const lat = clamped;
												if (long !== undefined && lat !== undefined) {
													setViewport({
														...viewport,
														latitude: lat,
													});
													setMarker({
														...marker,
														latitude: lat,
													});
												}
											}
										} catch (error) {
											console.error('150', error);
										}
									}}
								/>
							</Form.Item>
							<Form.Item
								name="zoom"
								label="Zoom level"
								rules={[{ required: true, message: 'Please input the zoom' }]}
							>
								<Input
									type="number"
									size="small"
									min={0}
									max={22}
									formatter={(value) => {
										if (value !== undefined) {
											return `${Math.round(value)}`;
										}
										return '';
									}}
									parser={(value) => {
										const intValue = parseInt(value || '');
										if (isNaN(intValue)) {
											return 0;
										}
										return intValue;
									}}
									style={{ width: '100%' }}
									onBlur={() => {
										setViewport({
											...viewport,
											zoom: form.getFieldValue('zoom'),
										});
									}}
								/>
							</Form.Item>
							<Form.Item
								name="visible"
								label="Active"
								valuePropName="checked"
								rules={[{ required: true }]}
							>
								<Toggle checkedText="ON" uncheckedText="OFF" size="small" />
							</Form.Item>
							<Form.Item
								name="networkType"
								label="Network Type"
								initialValue={null}
								rules={[{ required: false }]}
								trigger="onValueChange"
							>
								<Combobox size="small" items={networkTypeOptions} searchable={false}>
									<ComboboxInput
										placeholder="Select a Network"
										leftIcon={renderNetworkIcon(selectedNetworkType)}
									/>
									<ComboboxContent>
										<ComboboxEmpty>No options available.</ComboboxEmpty>
										<ComboboxList>
											{(item) => (
												<ComboboxItem key={item.value} value={item} leftIcon={item.icon}>
													{item.label}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</Form.Item>
						</Form>
					</div>
					<div className="modal-create-location__map-container flex items-center">
						<Map
							{...viewport}
							mapboxAccessToken={mapboxApiAccessToken}
							mapStyle={mapStyle}
							minZoom={0}
							maxZoom={22}
							style={{ width: '100%', height: '80%' }}
							onMove={(evt) => setViewport(evt.viewState)}
							onClick={(e) => {
								setMarker({
									latitude: e.lngLat.lat,
									longitude: e.lngLat.lng,
								});
								form.setFieldsValue({
									latitude: e.lngLat.lat,
									longitude: e.lngLat.lng,
								});
								setCoordinates({
									latitude: e.lngLat.lat,
									longitude: e.lngLat.lng,
								});
							}}
						>
							{marker && (
								<Marker latitude={marker.latitude} longitude={marker.longitude} offset={[-20, -10]}>
									<Pin size={30} />
								</Marker>
							)}
							<NavigationControl />
						</Map>
					</div>
				</div>
			</Modal.Content>

			<Modal.Footer>
				<Button
					label="Cancel"
					type="primary"
					shape="round"
					showIcon={false}
					style={{
						width: '85px',
						height: '40px',
						backgroundColor: '#F7F9FA',
						color: '#434B56',
						border: '1px solid #C4CCD5',
					}}
					onClick={() => {
						form.resetFields();
						setMarker({ latitude: 0, longitude: 0 });
						setCoordinates({ latitude: 0, longitude: 0 });
						setViewport(initialViewState);
						onCancel();
					}}
				/>

				<Button
					label="Accept"
					type="primary"
					shape="round"
					showIcon={false}
					style={{
						width: '85px',
						height: '40px',
						backgroundColor: '#00203C',
						color: 'white',
					}}
					onClick={() => form.submit()}
				/>
			</Modal.Footer>
		</Modal>
	);
}
